import React from 'react';
import { format } from 'date-fns';
import { AIReport } from '../../services/ai-reports.service';
import { REPORTS_PAGE_SIZE, ReportStatusValue, reportStatus } from '../../hooks/useReportQueries';
import { formatDateRange } from '../../utils/dateTime';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

export interface ReportsTableProps {
  rows: AIReport[];
  count: number;
  page: number;
  isPending: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onDownload: (id: string, format: 'html' | 'pdf' | 'docx') => void;
  onPageChange: (page: number) => void;
}

const STATUS_LABEL: Record<ReportStatusValue, string> = {
  GENERATING: 'Generating',
  FAILED: 'Failed',
  COMPLETED: 'Completed',
};

// AIReport has no distinct 'pending' state (generation is synchronous — see
// useReportQueries), but the default branch below covers it too if the union
// ever grows: only FAILED and COMPLETED get their own tone.
const statusToneClass = (status: ReportStatusValue) => {
  if (status === 'FAILED') return 'text-sev-critical';
  if (status === 'COMPLETED') return 'text-ink';
  return 'text-sev-high';
};

const DOWNLOAD_FORMATS: Array<{ value: 'html' | 'pdf' | 'docx'; label: string }> = [
  { value: 'html', label: 'HTML' },
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DOCX' },
];

const createdStamp = (createdAt: string) => format(new Date(createdAt), 'MMM d, yyyy').toUpperCase();

export const ReportsTable: React.FC<ReportsTableProps> = (props) => {
  const { rows, count, page, isPending, hasNext, hasPrevious } = props;

  if (isPending) return <LedgerSkeleton lines={8} className="mt-4" />;

  if (rows.length === 0) {
    return (
      <p className="py-6 font-serif text-scale-1 italic text-ink-muted">
        No reports on file yet.
      </p>
    );
  }

  const pageCount = Math.max(1, Math.ceil(count / REPORTS_PAGE_SIZE));
  const remaining = Math.max(0, count - page * REPORTS_PAGE_SIZE);

  return (
    <div>
      <table className="w-full border-collapse" aria-label="Reports">
        <thead>
          <tr className="border-b border-ink">
            <th className="cap-label py-1 text-left font-normal">Title</th>
            <th className="cap-label py-1 text-left font-normal">Status</th>
            <th className="cap-label py-1 text-left font-normal">Created</th>
            <th className="cap-label py-1 text-left font-normal">Downloads</th>
            <th className="cap-label py-1 text-left font-normal" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((report) => {
            const status = reportStatus(report);
            return (
              <tr
                key={report.id}
                data-testid="report-row"
                onClick={() => props.onOpen(report.id)}
                className="cursor-pointer border-b border-rule-faint text-scale-1 hover:bg-accent-soft"
              >
                <td className="py-1.5 pr-3">
                  <span className="block font-medium text-ink">
                    {formatDateRange(report.date_from, report.date_to)}
                  </span>
                  <span className="block text-scale-0 text-ink-muted">{report.organization_name}</span>
                </td>
                <td className="py-1.5 pr-3">
                  <span className={cn('cap-label', statusToneClass(status))}>{STATUS_LABEL[status]}</span>
                </td>
                <td className="py-1.5 pr-3">
                  <span className="font-mono text-scale-0 text-ink-muted">{createdStamp(report.created_at)}</span>
                </td>
                <td className="py-1.5 pr-3">
                  {status === 'COMPLETED' && (
                    <span className="flex items-baseline gap-1.5">
                      {DOWNLOAD_FORMATS.map((fmt, index) => (
                        <React.Fragment key={fmt.value}>
                          {index > 0 && <span aria-hidden className="text-ink-muted">·</span>}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              props.onDownload(report.id, fmt.value);
                            }}
                            className="cap-label cursor-pointer hover:!text-accent"
                          >
                            {fmt.label}
                          </button>
                        </React.Fragment>
                      ))}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onDelete(report.id);
                    }}
                    className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-serif text-scale-1 italic text-ink-muted">
          {remaining > 0 ? `${remaining} more entries` : `${count} report${count === 1 ? '' : 's'} on file`}
        </span>
        <span className="flex items-baseline gap-3">
          <button type="button" disabled={!hasPrevious} onClick={() => props.onPageChange(page - 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            ← Prev
          </button>
          <span className="cap-label">Page {page} / {pageCount}</span>
          <button type="button" disabled={!hasNext} onClick={() => props.onPageChange(page + 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            Next →
          </button>
        </span>
      </div>
    </div>
  );
};
